import { useMemo, useState } from 'react'
import { Download, ExternalLink, FileText } from 'lucide-react'
import {
  Button,
  Chip,
  EmptyState,
  ExportDrawer,
  FullscreenButton,
} from '@/components/ui'
import { odooUrl } from '@/lib/utils/odoo-url'
import { usePricelistItems } from '../api/queries'
import type { OdooM2O, PricelistItem, PricelistSummary } from '../api/types'
import { COLUMNS as PRICELIST_EXPORT_COLUMNS, slugForFilename } from '../utils/csv'
import { PricelistEditor } from './PricelistEditor'

const API_BASE = import.meta.env.VITE_API_BASE ?? ''

/** Sprint 37 — branded, customer-ready PDF of the full pricelist (not
 * just changed rows). Ops downloads and attaches it to their own
 * outbound email; no code path here sends anything to a customer. */
function pricelistPdfExportUrl(pricelistId: number): string {
  return `${API_BASE}/api/pricelists/${pricelistId}/export/pdf`
}

export interface PricelistDetailProps {
  pricelist: PricelistSummary
  /**
   * When true, the detail renders read-only with no grid editor.
   * Used by the Sprint 01b `?partner=N` deep-link when resolution lands
   * on the system default pricelist (D-8 — editing the default affects
   * every partner without an override, so the UI routes to Odoo
   * backoffice instead).
   */
  readOnly?: boolean
  /**
   * Optional banner rendered above the items section. Sprint 01b uses it
   * to indicate "this is the system default pricelist" when `readOnly`
   * is set via the deep-link path.
   */
  banner?: React.ReactNode
  /** Page-owned fullscreen state — drives the toolbar toggle icon. */
  fullscreen?: boolean
  /** Page-owned fullscreen toggle handler. */
  onToggleFullscreen?: () => void
}

/**
 * Sprint 01b checkpoint 2 — composes `PricelistEditor` for fixed-compute
 * items. Non-fixed rules render read-only inside the editor (handled by
 * `PricelistEditor`). The read-only mode path shows the items in a
 * plain table with no edit affordances.
 */
export function PricelistDetail({
  pricelist,
  readOnly = false,
  banner,
  fullscreen,
  onToggleFullscreen,
}: PricelistDetailProps) {
  const { data, isLoading, isFetching, isError, refetch } = usePricelistItems(pricelist.id)
  const items = useMemo<PricelistItem[]>(() => data?.items ?? [], [data])
  const [exportOpen, setExportOpen] = useState(false)

  // Export source = fixed-price items only. Non-fixed rules (percentage,
  // formula) are read-only on screen and intentionally excluded — there
  // is no useful unit price to dump for them.
  const fixedItems = useMemo<PricelistItem[]>(
    () => items.filter((it) => it.compute_price === 'fixed'),
    [items],
  )

  return (
    <div className="flex flex-col gap-4">
      <HeaderCard
        pricelist={pricelist}
        fetching={isFetching}
        onRefresh={() => refetch()}
        onExport={() => setExportOpen(true)}
        exportDisabled={fixedItems.length === 0}
        onExportPdf={() => window.open(pricelistPdfExportUrl(pricelist.id), '_blank')}
        fullscreen={fullscreen}
        onToggleFullscreen={onToggleFullscreen}
      />
      {banner}
      {isLoading ? (
        <p className="text-sm text-tertiary">Loading items…</p>
      ) : isError ? (
        <EmptyState
          variant="error"
          title="Couldn't load items"
          description="The /api/pricelists/{id}/items endpoint returned an error. Try Refresh."
        />
      ) : readOnly ? (
        <ReadOnlyItemsTable items={items} />
      ) : (
        <PricelistEditor
          pricelist={pricelist}
          items={items}
          refetch={() => refetch()}
          fetching={isFetching}
        />
      )}

      <ExportDrawer
        open={exportOpen}
        onOpenChange={setExportOpen}
        rows={fixedItems}
        columns={PRICELIST_EXPORT_COLUMNS}
        moduleId="customer-pricelists"
        filenameBase={`pricelist-${slugForFilename(pricelist.name)}`}
      />
    </div>
  )
}

function HeaderCard({
  pricelist,
  fetching,
  onRefresh,
  onExport,
  exportDisabled,
  onExportPdf,
  fullscreen,
  onToggleFullscreen,
}: {
  pricelist: PricelistSummary
  fetching: boolean
  onRefresh: () => void
  onExport: () => void
  exportDisabled: boolean
  onExportPdf: () => void
  fullscreen?: boolean
  onToggleFullscreen?: () => void
}) {
  return (
    <section
      data-test="pricelist-header-band"
      className="flex items-center justify-between gap-3 rounded-sm border border-border-subtle bg-surface-sunk px-3 py-2 text-sm"
    >
      <div className="flex min-w-0 flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="text-role-data-primary truncate">{pricelist.name}</span>
        <span className="text-tertiary">·</span>
        <span className="text-secondary">{m2oLabel(pricelist.currency_id) ?? '—'}</span>
        {pricelist.partner_id ? (
          <>
            <span className="text-tertiary">·</span>
            <span className="text-secondary truncate">{m2oLabel(pricelist.partner_id)}</span>
          </>
        ) : null}
        <span className="text-tertiary">·</span>
        <span className="text-secondary">
          {pricelist.item_count.toLocaleString()} items
        </span>
        {!pricelist.active ? (
          <Chip size="xs" variant="warning">
            Inactive
          </Chip>
        ) : null}
      </div>
      <div className="flex flex-shrink-0 items-center gap-2">
        <a
          href={odooUrl('product.pricelist', pricelist.id)}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-xs text-link hover:underline"
        >
          Open in Odoo <ExternalLink size={10} />
        </a>
        <button
          type="button"
          onClick={onRefresh}
          disabled={fetching}
          className="inline-flex cursor-pointer items-center rounded-xs border border-border-default bg-surface px-2 py-0.5 text-2xs uppercase tracking-widest text-secondary hover:bg-surface-hover disabled:cursor-not-allowed disabled:opacity-60"
        >
          {fetching ? 'Refreshing…' : 'Refresh'}
        </button>
        {onToggleFullscreen ? (
          <FullscreenButton
            fullscreen={Boolean(fullscreen)}
            onToggle={onToggleFullscreen}
          />
        ) : null}
        <Button
          variant="secondary"
          size="sm"
          onClick={onExportPdf}
          title="Download a branded, customer-ready PDF of this pricelist to attach to an email"
        >
          <FileText size={12} /> Export PDF
        </Button>
        <Button
          variant="secondary"
          size="sm"
          onClick={onExport}
          disabled={exportDisabled}
          title="Pick columns or apply a saved template before download"
        >
          <Download size={12} /> Export…
        </Button>
      </div>
    </section>
  )
}

function ReadOnlyItemsTable({ items }: { items: PricelistItem[] }) {
  return (
    <div className="overflow-x-auto rounded-sm border border-border-subtle bg-surface">
      <table className="w-full border-collapse text-sm">
        <thead className="border-b border-border-subtle bg-surface-sunk">
          <tr className="text-left">
            <Th>Product</Th>
            <Th>Customer part #</Th>
            <Th align="right">Min qty</Th>
            <Th align="right">Fixed price</Th>
            <Th>Valid from</Th>
            <Th>Valid to</Th>
            <Th>Manufacturer</Th>
            <Th>Country</Th>
            <Th>Compute</Th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr
              key={item.id}
              className="border-b border-border-subtle last:border-b-0 hover:bg-surface-hover"
            >
              <Td>
                <ProductLabelWithOdooLink item={item} />
              </Td>
              <Td>{item.customer_part_number || '—'}</Td>
              <Td align="right">
                {formatQty(item.min_quantity)}
              </Td>
              <Td align="right">
                {formatPrice(item.fixed_price)}
              </Td>
              <Td>{(item.date_start as string | false) || '—'}</Td>
              <Td>{(item.date_end as string | false) || '—'}</Td>
              <Td>{m2oLabel(item.manufacturer_id) ?? (item.manufacturer_trade_name || '—')}</Td>
              <Td>{m2oLabel(item.country_of_origin_id) ?? '—'}</Td>
              <Td>
                {item.compute_price === 'fixed' ? (
                  <Chip size="xs" variant="neutral">
                    fixed
                  </Chip>
                ) : (
                  <Chip size="xs" variant="warning" title="Edit in Odoo backoffice">
                    {item.compute_price}
                  </Chip>
                )}
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function Th({
  children,
  align = 'left',
}: {
  children: React.ReactNode
  align?: 'left' | 'right'
}) {
  return (
    <th
      className={`px-3 py-2 text-role-section-label ${
        align === 'right' ? 'text-right' : ''
      }`}
    >
      {children}
    </th>
  )
}

function Td({
  children,
  align = 'left',
}: {
  children: React.ReactNode
  align?: 'left' | 'right'
}) {
  return (
    <td
      className={`px-3 py-1.5 text-primary ${
        align === 'right' ? 'text-right tabular-nums' : ''
      }`}
    >
      {children}
    </td>
  )
}

function m2oLabel(m2o: OdooM2O | undefined): string | null {
  if (!m2o) return null
  return m2o[1] ?? null
}

/**
 * Renders the product name plus a small ExternalLink icon that opens
 * the Odoo product record in a new tab. Variant id wins when present;
 * SPI's customer pricelists are mostly template-scoped so the template
 * path is the common case.
 */
function ProductLabelWithOdooLink({ item }: { item: PricelistItem }) {
  const variant = item.product_id || undefined
  const tmpl = item.product_tmpl_id || undefined
  const variantId = variant && variant[0] ? variant[0] : null
  const tmplId = tmpl && tmpl[0] ? tmpl[0] : null
  const label = m2oLabel(variant) ?? m2oLabel(tmpl) ?? null
  if (!label) return <span className="text-tertiary">—</span>
  const odooHref = variantId
    ? odooUrl('product.product', variantId)
    : tmplId
      ? odooUrl('product.template', tmplId)
      : null
  return (
    <span className="inline-flex items-center gap-1">
      <span>{label}</span>
      {odooHref ? (
        <a
          href={odooHref}
          target="_blank"
          rel="noreferrer"
          aria-label={`Open ${label} in Odoo`}
          title="Open in Odoo"
          onClick={(e) => e.stopPropagation()}
          className="inline-flex items-center text-tertiary hover:text-primary"
        >
          <ExternalLink size={10} aria-hidden="true" />
        </a>
      ) : null}
    </span>
  )
}

function formatQty(q: number | null | undefined): string {
  if (q === null || q === undefined) return '—'
  return q.toLocaleString()
}

function formatPrice(p: number | false | null | undefined): string {
  if (p === null || p === undefined || p === false) return '—'
  return p.toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 4,
  })
}
